import { useMutation } from '@tanstack/react-query'
import { authApi } from '@/features/auth/api/auth.api'
import { authStore } from '@/features/auth/services/auth-store'
import {
  isAuthenticatedLogin,
  isPasswordResetRequiredLogin,
  type LoginResponse,
} from '@/shared/types/api-token'
import {
  type ApiErrorPayload,
  AuthErrorCode,
  AuthHttpStatus,
} from '../model/auth.errors'

import axios, { type AxiosError } from 'axios'

interface LoginVariables {
  username: string
  password: string
  remember: boolean
}

export type LoginErrorResult =
  | { display: 'toast'; title: string; key: string }
  | { display: 'field'; key: string }

export type LoginOutcome =
  | { kind: 'authenticated' }
  | {
      kind: 'password_reset_required'
      resetToken: string
      resetTokenExpiresIn: number
    }

function toOutcome(resp: LoginResponse, remember: boolean): LoginOutcome {
  if (isAuthenticatedLogin(resp)) {
    authStore.setAccessToken(resp.access_token, resp.expires_in)
    authStore.setSessionActive(remember)
    return { kind: 'authenticated' }
  }

  if (isPasswordResetRequiredLogin(resp)) {
    return {
      kind: 'password_reset_required',
      resetToken: resp.reset_token,
      resetTokenExpiresIn: resp.reset_token_expires_in,
    }
  }

  throw new Error('Unexpected login response shape')
}

export function useLogin() {
  const resolveError = (err: unknown): LoginErrorResult => {
    if (!axios.isAxiosError<ApiErrorPayload>(err) || !err.response)
      return {
        display: 'toast',
        title: 'login:toast.network_error_title',
        key: 'toast.network_error_description',
      }

    const code = err.response?.data?.error
    const status = err.response.status

    if (
      status === AuthHttpStatus.UNAUTHORIZED ||
      code === AuthErrorCode.UNAUTHORIZED
    )
      return { display: 'field', key: 'login:errors.invalid_credentials' }

    if (code === AuthErrorCode.USER_PENDING_ACTIVATION)
      return {
        display: 'field',
        key: 'auth-common:account_errors.pending_confirmation',
      }

    if (
      code === AuthErrorCode.USER_INACTIVE ||
      code === AuthErrorCode.USER_BLOCKED
    )
      return {
        display: 'field',
        key: 'auth-common:account_errors.user_not_activated',
      }

    if (
      status === AuthHttpStatus.TOO_MANY_REQUESTS ||
      code === AuthErrorCode.IP_RATE_LIMIT_EXCEEDED
    )
      return { display: 'field', key: 'login:errors.ip_rate_limit' }

    return { display: 'field', key: 'auth-common:form.errors.general_error' }
  }

  const authentication = useMutation<
    LoginOutcome,
    AxiosError<ApiErrorPayload>,
    LoginVariables
  >({
    mutationFn: async ({ username, password, remember }) => {
      const resp = await authApi.login(username, password)
      return toOutcome(resp, remember)
    },
  })

  return { resolveError, authentication }
}
